import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { UserMenu } from './UserMenu.js';
import { CreateIssueDialog } from '@/features/issues/CreateIssueDialog.js';

const navItems = [
  { to: '/projects', label: 'Projects' },
  { to: '/boards', label: 'Boards' },
  { to: '/search', label: 'Search' },
  { to: '/following', label: 'Following' },
];

export function Layout() {
  const [createOpen, setCreateOpen] = useState(false);
  const fetchingCount = useIsFetching();

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-blue-50 text-blue-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="sticky top-0 z-20 border-b bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-14 w-full items-center justify-between gap-4 px-4 sm:px-6" style={{ maxWidth: '100rem' }}>
          <div className="flex min-w-0 items-center gap-6">
            <NavLink to="/" className="flex shrink-0 items-center gap-2 text-gray-900">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-600 text-white" aria-hidden="true">
                <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor">
                  <path d="M8 1.5l6.5 6.5L8 14.5 1.5 8zm0 3.2L4.7 8 8 11.3 11.3 8z" />
                </svg>
              </span>
              <span className="text-base font-semibold">Jira Portal</span>
            </NavLink>
            <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} className={navClass}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {fetchingCount > 0 && (
              <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24" aria-label="Loading">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
                <path d="M8 3v10M3 8h10" strokeLinecap="round" />
              </svg>
              Create
            </button>
            <UserMenu />
          </div>
        </div>
        {/* Compact nav for small screens */}
        <nav className="flex gap-1 overflow-x-auto border-t px-3 py-1.5 md:hidden" aria-label="Main">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={navClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full flex-1 px-4 py-6 sm:px-6" style={{ maxWidth: '100rem' }}>
        <Outlet />
      </main>

      <CreateIssueDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
